import dayjs from 'dayjs';
import { z } from 'zod';

export const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$|@/;
export const HTTP_URL_REGEX =
  /^https:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
export const GITHUB_REGEX = /^(https?:\/\/)?(www\.)?github\.com\/[\w-]+/i;
export const LINKEDIN_REGEX = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+/i;

const dateStringOrDayjsSchema = z.union([
  z.string().refine((val) => dayjs(val, 'YYYY-MM-DD', true).isValid(), {
    message: 'Invalid date string format. Expected YYYY-MM-DD.',
  }),
  z.any().refine((val) => dayjs.isDayjs(val), { message: 'Invalid Dayjs object' }),
]);

export const MainFormSchema = z
  .object({
    description: z.string().min(3, { message: 'Must be more than 3 characters' }),
    email: z
      .union([z.literal(''), z.string().email({ message: 'Must be a valid email' })])
      .optional(),
    jobTitle: z.string().min(3, { message: 'Must be more than 3 characters' }),
    location: z.string().optional(),
    phoneNumber: z
      .union([
        z.literal(''),
        z.string().max(20, { message: 'Phone number must be at most 20 characters' }),
      ])
      .optional(),
    profileName: z
      .string()
      .min(3, { message: 'Must be more than 3 characters' })
      .refine((value) => !EMAIL_REGEX.test(value), 'Name cannot be an email address'),
    totalExperience: z.coerce
      .number({
        invalid_type_error: 'Experience must be a number',
        required_error: 'Experience is required',
      })
      .positive({
        message: 'Years of experience must be greater than 0',
      })
      .transform((total) => total.toString()),
  })
  .required();

export type MainFormSchemaType = z.infer<typeof MainFormSchema>;

export const HireFormSchema = z.object({
  contractor: z.boolean().optional(),
  employment: z.boolean().optional(),
  freelancer: z.boolean().optional(),
  fulltime: z.boolean().optional(),
  hybrid: z.boolean().optional(),
  isAvailableForHire: z.boolean(),
  ondemand: z.boolean().optional(),
  onsite: z.boolean().optional(),
  parttime: z.boolean().optional(),
  remote: z.boolean().optional(),
});

export type HireFormSchemaType = z.infer<typeof HireFormSchema>;

export const RelocateFormSchema = z
  .object({
    interestedToRelocateTo: z.array(z.string()),
    isInterestedInRelocation: z.boolean(),
  })
  .required();

export type RelocateFormSchemaType = z.infer<typeof RelocateFormSchema>;

export const RateFormSchema = z
  .object({
    hourlyRate: z.string().nullish(),
    minimumHourlyRate: z.string().nullish(),
    minimumMonthlyRate: z.string().nullish(),
    monthlyRate: z.string().nullish(),
    rateCurrency: z.string().min(1, 'Rate Currency is required'),
  })
  .required()
  .partial()
  .refine(
    ({ hourlyRate, monthlyRate }) =>
      (hourlyRate !== '' && hourlyRate !== null) || (monthlyRate !== '' && monthlyRate !== null),
    {
      message: 'Either target hourly rate or target monthly rate must be filled',
      path: ['monthlyRate'],
    },
  )
  .refine(({ hourlyRate, monthlyRate }) => hourlyRate !== '' || monthlyRate !== '', {
    message: 'Either target hourly rate or target monthly rate must be filled',
    path: ['hourlyRate'],
  });

export type RateFormSchemaType = z.infer<typeof RateFormSchema>;

export const LanguageFormSchema = z
  .object({
    newLanguage: z.string().min(1, 'Language is required'),
    newRate: z.string(),
  })
  .required();

export type LanguageFormSchemaType = z.infer<typeof LanguageFormSchema>;

export const LinkFormSchema = z
  .object({
    id: z.string().optional(),
    label: z.string().optional(),
    url: z.string().min(1, 'URL is required'),
  })
  .superRefine((schema, ctx) => {
    if (schema.label === 'other' && !HTTP_URL_REGEX.test(schema.url)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'URL must be a valid URL if label is "other"',
        path: ['url'],
      });
    }

    if (
      schema.label === 'github' &&
      (!GITHUB_REGEX.test(schema.url) || schema.url.trim() === 'github.com/')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'URL must be a valid GitHub profile URL if label is "github"',
        path: ['url'],
      });
    }

    if (
      schema.label === 'linkedin' &&
      (!LINKEDIN_REGEX.test(schema.url) || schema.url.trim() === 'linkedin.com/')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'URL must be a valid LinkedIn profile URL if label is "linkedin"',
        path: ['url'],
      });
    }
  });

export type LinkFormSchemaType = z.infer<typeof LinkFormSchema>;

export const ExperienceFormSchema = z
  .object({
    company: z.string().min(1, 'Company name is required'),
    description: z.string().optional(),
    end: dateStringOrDayjsSchema.nullable(),
    id: z.string().optional(),
    location: z.string().nullable().optional(),
    ongoing: z.boolean().optional(),
    start: dateStringOrDayjsSchema.refine((date) => {
      return dayjs(date).isBefore(dayjs());
    }, 'The Start date must be before today'),
    title: z.string().min(1, 'Title is required'),
  })
  .required()
  .refine(
    (schema) => {
      if (schema.ongoing) {
        return true;
      }

      if (dayjs(schema.start).isBefore(dayjs(schema.end)) && dayjs(schema.end).isBefore(dayjs())) {
        return true;
      }

      return false;
    },
    {
      message: 'The End date must be between the Start date and today',
      path: ['end'],
    },
  );

export type ExperienceFormSchemaType = z.infer<typeof ExperienceFormSchema>;

export const EducationFormSchema = z
  .object({
    degree: z.string().min(1, 'Level is required'),
    description: z.string().optional(),
    end: dateStringOrDayjsSchema.nullable(),
    id: z.string().optional(),
    location: z.string().nullable().optional(),
    ongoing: z.boolean(),
    program: z.string().min(1, 'Program is required'),
    school: z.string().min(1, 'School name is required'),
    start: dateStringOrDayjsSchema.refine((date) => {
      return dayjs(date).isBefore(dayjs());
    }, 'The Start date must be before today'),
  })
  .required()
  .refine(
    (schema) => {
      if (schema.ongoing) {
        return true;
      }

      if (dayjs(schema.start).isBefore(dayjs(schema.end)) && dayjs(schema.end).isBefore(dayjs())) {
        return true;
      }

      return false;
    },
    {
      message: 'The End date must be between the Start date and today',
      path: ['end'],
    },
  );

export type EducationFormSchemaType = z.infer<typeof EducationFormSchema>;

export const ProfileSearchableFormSchema = z.object({
  isVisible: z.boolean().optional(),
});

export type ProfileSearchableFormSchemaType = z.infer<typeof ProfileSearchableFormSchema>;
